/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';
import MUIIconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import {makeStyles} from '@material-ui/core/styles';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import classnames from 'classnames';
import {getLanguage, translate} from '@ion/core/src/locale/locale';
import {Entity, GenericObject} from '@ion/core/src/types/entities';
import {BackButton} from '@ion/dls/src/components/buttons/BackButton';
import {ForwardButton} from '@ion/dls/src/components/buttons/ForwardButton';
import {ViewSubGroupsSwitch} from '@ion/dls/src/components/switch/ViewSubGroupsSwitch';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {
  DLSTheme,
  ViewSubGroupsProps
} from '@ion/dls/src/types/types';
import {UsePaginationResult} from '@ion/query/src/hooks/usePagination';
import Render from '../render/Render';
import useThemeContext from '../theme/useThemeContext';
import GoToPage, {PageConstraintsProps} from './GoToPage';
import {AgGridType} from './IonGrid';
import {getPageProxy, PaginationChange} from './IonGridUtils';
import PaginationTips from './PaginationTips';

export interface GridResult {
  [key: string]: any;
}

interface StyleProps {
  rowPerPageTextTransform?: any;
  loading?: boolean;
  hideSubGroups?: boolean;
  isDialogControl?: boolean;
  isCurrentTheme?: boolean;
  showPaginationState?: boolean;
};

export interface PaginationControlsType extends StyleProps, ViewSubGroupsProps {
  gridApi: AgGridType;
  pageSizes?: number[];
  pageSize: number;
  setPageSize?: Function;
  totalResultCount: number;
  onPageBack?: (event: MouseEvent<HTMLButtonElement>) => void;
  onPageForward?: (event: MouseEvent<HTMLButtonElement>) => void;
  onManualPaginationChange?: Function;
  paginationState?: {
    api?: AgGridType;
  };
  paging?: UsePaginationResult;
  isPrefetching?: boolean;
  disablePortal?: boolean;
  customPaperClass?: string;
  disablePaginateControls?: boolean;
  entity?: Entity;
  setDisablePaginateControls?: Function;
  pageConstraintProps?: PageConstraintsProps;
  paginationStyle?: GenericObject;
};

// defines the pagination container styles ...
const useStyles = makeStyles((theme: DLSTheme) => ({
  paginationContainer : toggleThemeStyle({
    gap                            : '0 16px',
    color                          : dlsColor('grey1'),
    display                        : 'flex',
    fontSize                       : '12px',
    borderTop                      : `solid 1px ${dlsColor('grey3')}`,
    alignItems                     : 'center',
    fontWeight                     : 400,
    lineHeight                     : '20px',
    flexDirection                  : 'unset',
    justifyContent                 : 'end',
    backgroundColor                : dlsColor('white'),
    ...theme.dls.typography.body3,
    [theme.breakpoints.down('sm')] : {
      paddingLeft    : '17px',
      paddingRight   : '17px',
      flexWrap       : 'wrap',
      justifyContent : 'space-evenly',
      paddingTop     : '12px'
    }
  }, {
    gap                            : '0 16px',
    color                          : dlsColor('grey3'),
    // the tips, the "Showing x of y" state and the controls in three
    // columns, the outer two equal - so the state sits at the grid's centre,
    // under its pull tab, however wide the tips and controls are ...
    display                        : 'grid',
    gridTemplateColumns            : '1fr auto 1fr',
    fontSize                       : '12px',
    borderTop                      : `solid 1px ${dlsColor('grey4')}`,
    alignItems                     : 'center',
    fontFamily                     : 'Inter !important',
    fontWeight                     : 500,
    lineHeight                     : '16px',
    backgroundColor                : dlsColor('white'),
    [theme.breakpoints.down('sm')] : {
      display        : 'flex',
      flexDirection  : 'row',
      flexWrap       : 'wrap',
      justifyContent : 'space-evenly',
      padding        : '16px'
    }
  }),
  legacyBottomMargin : {
    marginBottom : ({isDialogControl}: StyleProps) => !isDialogControl ? '40px' : '0px'
  },
  currentPaginationState : {
    gridColumn     : 2,
    alignItems     : 'center',
    color          : dlsColor('grey2'),
    display        : 'flex',
    fontFamily     : 'Inter',
    fontSize       : '12px',
    fontWeight     : 500,
    justifyContent : 'center',
    whiteSpace     : 'nowrap'
  },
  paginationSubgroups : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  paginationPageSize : toggleThemeStyle({
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center'
  }, {
    color          : dlsColor('grey2'),
    display        : 'flex',
    fontSize       : '12px',
    alignItems     : 'center',
    fontFamily     : 'Inter',
    fontWeight     : 500,
    lineHeight     : '16px',
    justifyContent : 'center'
  }),
  paginationPageSizeSelect : {
    position : 'relative',
    '& div'  : {
      '&:hover:not(.Mui-disabled)' : {
        '&:before' : {
          borderBottom : 'none'
        }
      },
      '&:before' : {
        borderBottom : 'none'
      },
      '&:after' : {
        borderBottom : 'none'
      }
    },
    [theme.breakpoints.down('sm')] : {
      '& select' : {
        paddingLeft  : '7px',
        paddingRight : '7px'
      }
    }
  },
  pageSizeOption : {
    padding   : '5px 8px !important',
    textAlign : 'center'
  },
  paginationPaging : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    marginRight    : '4px'
  },
  paginationPageArrow : {
    [theme.breakpoints.down('sm')] : {
      '& button' : {
        paddingLeft  : '7px',
        paddingRight : '7px'
      }
    }
  },
  selectList : {
    padding : '5px !important'
  },
  paper : {
    minWidth : '75px !important',
    width    : '75px'
  },
  selectIcon : {
    top : 'calc(50% - 15px)'
  },
  select : toggleThemeStyle({
    ...theme.dls?.typography?.pagination
  }, {
    color      : dlsColor('grey2'),
    padding    : '4px',
    fontSize   : '12px',
    fontFamily : 'Inter',
    fontWeight : 500,
    lineHeight : '16px',
    marginLeft : '10px'
  }),
  paginationPageSizeText : {
    textTransform                  : ({rowPerPageTextTransform}: StyleProps) => rowPerPageTextTransform ?? 'uppercase',
    [theme.breakpoints.down('sm')] : {
      maxWidth     : '100px',
      overflow     : 'hidden',
      textOverflow : 'ellipsis',
      whiteSpace   : 'nowrap'
    }
  },
  pageSizeItem : {
    '&:hover' : {
      backgroundColor : dlsColor('paleBlue')
    }
  },
  selectedPageSize : {
    '&&' : {
      backgroundColor : dlsColor('paleBlue'),
      '&:hover'       : {
        backgroundColor : dlsColor('paleBlue')
      }
    }
  },
  navigation : {
    display                        : 'flex',
    justifyContent                 : toggleThemeStyle('center', 'space-around'),
    alignItems                     : 'center',
    gap                            : '4px',
    width                          : toggleThemeStyle('auto', '100%'),
    [theme.breakpoints.down('sm')] : {
      justifyContent : 'space-between !important',
      width          : ({hideSubGroups}: StyleProps) => hideSubGroups ? '100%' : 'auto'
    }
  },
  topRow : {
    display                        : 'flex',
    justifyContent                 : 'space-between',
    alignItems                     : 'center',
    gap                            : '12px',
    [theme.breakpoints.down('sm')] : {
      width : '100%'
    }
  },
  bottomRow : {
    minWidth                       : '171px',
    display                        : 'flex',
    justifyContent                 : 'space-between',
    flexWrap                       : 'nowrap',
    [theme.breakpoints.down('sm')] : {
      width : '100%'
    }
  },
  controlsWrapper : {
    gap                            : '4px',
    flex                           : '0 0 auto',
    // the last of the current theme's three columns, at its right end ...
    gridColumn                     : 3,
    justifySelf                    : 'end',
    display                        : 'flex',
    alignItems                     : 'center',
    justifyContent                 : 'flex-end',
    [theme.breakpoints.down('sm')] : {
      flexDirection : 'column',
      width         : '100%',
      overflow      : 'hidden'
    }
  }
}));

/**
 * The utility function returns the paginatation info object which includes page size,
 * total page, current page, next/previous availability. These information derived from
 * the React-Query paging mechanism.
 *
 * ```typescript
 *
 *  import {getDefaultPagingState} from '@ion/dls/src/grid/';
 *
 *  const {
 *    pageSize,
 *    hasNext,
 *    hasPrevious,
 *    topDisplayedRowIndex,
 *    bottomDisplayedRowIndex
 *  } = getDefaultPagingState(paging);
 *
 * ```
 */
export const getDefaultPagingState = (paging: UsePaginationResult) => {
  const {
    pageSize,
    hasPrevious,
    hasNext,
    total,
    page
  } = paging;
  const lastRow = (page * pageSize) - 1;

  return {
    active                  : false,
    topDisplayedRowIndex    : ((page - 1) * pageSize),
    bottomDisplayedRowIndex : lastRow < total ? lastRow : total - 1,
    totalPage               : Math.ceil(total / pageSize),
    pageSize,
    hasNext,
    hasPrevious
  };
};

/**
 * The utility function returns the paginatation info object which includes page size,
 * total page, current page, next/previous availability. These information derived from
 * the React-Query paging mechanism.
 *
 * ```typescript
 *
 *  import {getLegacyPagingState} from '@ion/dls/src/grid/';
 *
 *  const {
 *    pageSize,
 *    hasNext,
 *    hasPrevious,
 *    topDisplayedRowIndex,
 *    bottomDisplayedRowIndex
 *  } = getDefaultPagingState(paging, totalCount);
 *
 * ```
 */
export const getLegacyPagingState = (paging: GenericObject, totalResultCount: number) => {
  const {
    currentPage = 0,
    pageSize = 20,
    bottomDisplayedRowIndex = 0,
    topDisplayedRowIndex = 0
  } = getPageProxy(paging, totalResultCount);
  const totalPage = Math.ceil(totalResultCount / pageSize);

  return {
    active      : (totalPage === 0),
    hasNext     : !((currentPage + 1) === totalPage) || (totalResultCount === 0),
    hasPrevious : !(currentPage === 0),
    pageSize,
    totalPage,
    bottomDisplayedRowIndex,
    topDisplayedRowIndex
  };
};

/**
 * @PaginationControls
 *
 * Defines the custom pagination bar that is attached to the IonGrid if the user enables it
 *
 * @param {Array<number>} pageSizes the options in the select menu to
 *   set the page size
 * @param {object} gridApi object that contains the grid's api
 * @param {object} paginationState Hook function that sets the values of the gridRows
 * @param {Function} setPageSize function that sets the state with the page size
 * @param {Function} onPageBack function that gets called conditionally
 *   if passed when user pages back
 * @param {Function} onPageForward function that gets called conditionally
 *   if passed when user pages forward
 * @param {number} totalResultCount the total number of assets returned for this account,
 * regardless of page size
 * @param {boolean} viewSubGroups value that determines if subgroups should be fetched and shown
 * @param {Function} toggleViewSubGroups Hook function that sets the value of viewSubGroups
 *
 *
 * ```typescript
 *
 *   <PaginationControls gridApi={gridApi}
 *     gridRows={gridRows}
 *     setGridRows={setGridRows}
 *     paginationState={paginationState}
 *     totalResultCount={totalResultCount}
 *     setTotalResultCount={setTotalResultCount}
 *     getItems={getItems}
 *     setPageSize={setPageSize}
 *     gridPaginationSizes={gridPaginationSizes}
 *     loadItems={loadItems} />
 *
 * ```
 *
 */
export const PaginationControls = forwardRef(({
  gridApi,
  pageSizes,
  pageSize,
  setPageSize,
  totalResultCount,
  onPageBack,
  onPageForward,
  paginationState,
  viewSubGroups,
  customPaperClass,
  toggleViewSubGroups,
  onManualPaginationChange,
  paging,
  paginationStyle,
  preference,
  entity,
  disablePaginateControls,
  setDisablePaginateControls,
  pageConstraintProps,
  loading = false,
  disablePortal = false,
  isPrefetching = false,
  hideSubGroups = false,
  isDialogControl = false,
  isCurrentTheme = false,
  showPaginationState = true,
  rowPerPageTextTransform = 'uppercase'
}: PaginationControlsType, ref) => {
  const classes = useStyles({
    hideSubGroups : hideSubGroups || !toggleViewSubGroups,
    rowPerPageTextTransform,
    isDialogControl
  });

  const {isPhone}                 = useMedia();
  const {isLegacy: isLegacyTheme} = useThemeContext();
  const isDisabled                = useRef(false);

  useEffect(() => {
    if (!loading) {
      setDisablePaginateControls?.(false);
    }
  }, [loading]);

  const setPaginationEnabledState = useCallback((disabled = false) => {
    isDisabled.current = disabled;
  }, []);

  useImperativeHandle(ref, () => ({
    setPaginationEnabledState
  }), []);

  const {
    active,
    hasNext,
    totalPage,
    hasPrevious,
    bottomDisplayedRowIndex,
    topDisplayedRowIndex
  } = useMemo(() => {
    if (paging) {
      return getDefaultPagingState(paging);
    }
    return gridApi ? getLegacyPagingState(gridApi, totalResultCount) : {} as any;
  }, [paging, paginationState, gridApi, totalResultCount]);

  const toggleSubGroupsView = useCallback(() => {
    toggleViewSubGroups?.(!viewSubGroups);
  }, [viewSubGroups]);

  /**
   * @handlePageSizeChange
   *
   * If we already have the data we can simply change the page
   * size. If not, we need to get the row items we need.
   */
  async function handlePageSizeChange({target: {value}}) {
    setPageSize(value);
    if (paging) {
      const {gotoPage, setPageSize} = paging;
      gotoPage(1);
      setPageSize(value);
    }
    onManualPaginationChange?.(PaginationChange.PAGE_SIZE_CHANGE);
  }

  /**
   * @handlePageBack
   *
   * Since we don't need to fetch data we can simply execute
   * a page back.
   */
  function handlePageBack(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    gridApi.paginationGoToPreviousPage();

    onPageBack?.(e);
    setPaginationEnabledState(false);
    setDisablePaginateControls?.(true);
    paging?.previousPage();
    onManualPaginationChange?.();
  }

  /**
   * @handlePageForward
   *
   * When user signals to move forward calling the gridAPi to
   * paginate forward will automatically check if the cache has the
   * data and if not will fetch it
   */
  function handlePageForward(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    gridApi.paginationGoToNextPage();
    onPageForward?.(e);
    setPaginationEnabledState(false);
    setDisablePaginateControls?.(true);
    paging?.nextPage();
    onManualPaginationChange?.();
  };

  const renderPageValue = useCallback(
    (value: string) => translate('grids:pagination.rowsPerPage', {rows: value}
    ), [getLanguage(), translate]);

  const currentPaginationState = useMemo(() => {
    const shownRows = totalResultCount ? bottomDisplayedRowIndex + 1 : 0;
    const entityKey = entity ? `${entity}${String(entity).endsWith('s') ? '' : 's'}` : null;
    const entityLabel = entityKey
      ? translate(`common:entityLabels.${entityKey}`)
      : translate('grids:pagination.records');

    return translate('grids:pagination.currentStateWithEntity', {
      shownRows,
      totalRows : totalResultCount,
      entity    : entityLabel
    });
  }, [bottomDisplayedRowIndex, entity, totalResultCount, translate]);

  const subGroupToggle = useMemo(() => (
    <>
      {!hideSubGroups && toggleViewSubGroups && (
        <div className={classes.paginationSubgroups}>
          <ViewSubGroupsSwitch checked={viewSubGroups}
            onChange={toggleSubGroupsView}
            preference={preference}
            labelColor={dlsColor('grey2')}
            showLabel />
        </div>
      )}
    </>
  ), [
    hideSubGroups,
    toggleViewSubGroups,
    toggleSubGroupsView,
    viewSubGroups
  ]);

  return (
    <div className={classnames(classes.paginationContainer, {[classes.legacyBottomMargin]: isLegacyTheme})}
      style={paginationStyle}>
      <Render.If condition={isCurrentTheme && !isPhone}>
        <PaginationTips />
      </Render.If>
      <Render.If condition={isCurrentTheme && showPaginationState && !isPhone}>
        <div className={classes.currentPaginationState}>
          {currentPaginationState}
        </div>
      </Render.If>
      <div className={classes.controlsWrapper}>
        <div className={classes.topRow}>
          <Render.If condition={showPaginationState && !isCurrentTheme}>
            <div>
              {translate('grids:pagination.currentState', {
                firstRow : totalResultCount ? topDisplayedRowIndex + 1 : 0,
                lastRow  : totalResultCount ? bottomDisplayedRowIndex + 1 : 0,
                pageSize : totalResultCount
              })}
            </div>
          </Render.If>
          <Render.If condition={isCurrentTheme && showPaginationState && isPhone}>
            <div className={classes.currentPaginationState}>
              {currentPaginationState}
            </div>
          </Render.If>
          {!isPhone && (subGroupToggle)}
          <div className={classes.paginationPageSize}>
            <div className={classes.paginationPageSizeSelect}>
              <Select value={pageSize}
                renderValue={renderPageValue}
                classes={{
                  icon   : classes.selectIcon,
                  select : classes.select
                }}
                MenuProps={{
                  disablePortal : disablePortal,
                  classes       : {
                    list  : classes.selectList,
                    paper : classnames(classes.paper, disablePortal ? customPaperClass : '')
                  }
                }}
                onChange={handlePageSizeChange}>
                {pageSizes.map((val, i) => {
                  return (
                    <MenuItem value={val}
                      key={`grid-rows-option-${val}`}
                      classes={{
                        root     : classes.pageSizeOption,
                        selected : classes.selectedPageSize,
                        gutters  : classes.pageSizeItem
                      }}>
                      {val}
                    </MenuItem>
                  );
                })}
              </Select>
            </div>
          </div>
        </div>
        <div className={classes.bottomRow}>
          <div className={classes.navigation}>
            <div className={classes.paginationPageSizeText}>
              <GoToPage pageSize={pageSize}
                gridApi={gridApi}
                startRow={topDisplayedRowIndex}
                totalPage={totalPage}
                isCurrentTheme={isCurrentTheme}
                totalResultCount={totalResultCount}
                pageConstraintProps={pageConstraintProps}
                onManualPaginationChange={onManualPaginationChange}
                paging={paging}
                loading={loading} />
            </div>
            <div className={classes.paginationPaging}>
              <div className={classes.paginationPageArrow}>
                <Render>
                  <Render.If condition={isCurrentTheme}>
                    <BackButton onClick={handlePageBack}
                      disabled={!hasPrevious || isPrefetching || disablePaginateControls} />
                  </Render.If>
                  <Render.Else>
                    <MUIIconButton onClick={handlePageBack}
                      disabled={!hasPrevious || isPrefetching || disablePaginateControls}>
                      <ChevronLeft />
                    </MUIIconButton>
                  </Render.Else>
                </Render>
              </div>
              <div className={classes.paginationPageArrow} style={!isCurrentTheme ? {marginLeft: '-18px'} : undefined}>
                <Render>
                  <Render.If condition={isCurrentTheme}>
                    <ForwardButton onClick={handlePageForward}
                      disabled={!hasNext || isPrefetching || active || disablePaginateControls} />
                  </Render.If>
                  <Render.Else>
                    <MUIIconButton onClick={handlePageForward}
                      disabled={!hasNext || isPrefetching || active || disablePaginateControls}>
                      <ChevronRight />
                    </MUIIconButton>
                  </Render.Else>
                </Render>
              </div>
            </div>
          </div>
          {isPhone && (subGroupToggle)}
        </div>
      </div>
    </div>
  );
});

export default PaginationControls;
